import { VStack, Text } from '@skin-seoul/design-system';
import { useUser, transformOrderForDisplay, ORDER_QUERIES } from '@skin-seoul/service';
import { OrderCard, OrderCardSkeleton, LucideIcon } from '@skin-seoul/shared-components';
import { useQuery } from '@tanstack/react-query';

const OrderItem = ({ orderId }: { orderId: string }) => {
  const { data: order, isLoading, isError } = useQuery(ORDER_QUERIES.getOrder({ orderId: Number(orderId) }));

  if (isLoading) {
    return <OrderCardSkeleton />;
  }

  if (isError || !order) {
    return null;
  }

  return <OrderCard order={transformOrderForDisplay(order)} />;
};

const RegisteredDeliveryTracking = () => {
  const { user, isLoading: isUserLoading, isAuthenticated } = useUser();

  const { data: userOrders, isLoading: isOrdersLoading } = useQuery({
    ...ORDER_QUERIES.getUserOrders({ userId: user?.userId ?? 0 }),
    enabled: isAuthenticated && Boolean(user?.userId),
  });

  if (isUserLoading || isOrdersLoading) {
    return (
      <VStack width="100%" gap={4} padding={4}>
        <OrderCardSkeleton />
        <OrderCardSkeleton />
      </VStack>
    );
  }

  const hasOrders = userOrders?.orderIds && userOrders.orderIds.length > 0;

  if (!hasOrders) {
    return (
      <VStack
        width="100%"
        bg="generic.0"
        border="1px solid"
        borderColor="neutral.100"
        borderRadius="8px"
        padding={8}
        gap={3}
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
      >
        <LucideIcon name="Box" width={16} height={16} color="neutral.400" />
        <Text textStyle="heading.h5.mobile" fontWeight="semibold" color="neutral.700">
          No Orders Found
        </Text>
        <Text textStyle="paragraph.medium" color="neutral.400" textAlign="center">
          You haven&apos;t placed any orders yet.
        </Text>
      </VStack>
    );
  }

  return (
    <VStack width="100%" gap={4}>
      <VStack width="100%" gap={2} alignItems={{ base: 'center', tablet: 'flex-start' }}>
        <Text
          textStyle="heading.h6.mobile"
          fontWeight="medium"
          color="neutral.900"
          textAlign={{ base: 'center', tablet: 'left' }}
        >
          Orders
        </Text>
        <Text
          textStyle="paragraph.small"
          fontWeight="regular"
          color="neutral.400"
          textAlign={{ base: 'center', tablet: 'left' }}
        >
          Check your order and delivery status.
        </Text>
      </VStack>

      <VStack width="100%" gap={4}>
        {userOrders.orderIds.map((orderId) => (
          <OrderItem key={orderId} orderId={orderId} />
        ))}
      </VStack>
    </VStack>
  );
};

export default RegisteredDeliveryTracking;
